import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  User,
  Calendar,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Loader2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { conversationService } from "../../services/conversationService";
import type { Conversation } from "../../types/conversation";
import { Card } from "../ui/Card";

interface RecentConversationsProps {
  limit?: number;
  showTitle?: boolean;
}

export function RecentConversations({
  limit = 5,
  showTitle = true,
}: RecentConversationsProps): JSX.Element {
  const navigate = useNavigate();

  const {
    data: conversationsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["conversations", "recent", limit],
    queryFn: () =>
      conversationService.getConversations({
        page: 0,
        size: limit,
      }),
    refetchOnMount: "always",
  });

  const formatDate = (dateString: string): string => {
    // Ensure the date string is treated as UTC if it doesn't have timezone info
    const utcDateString = dateString.endsWith("Z")
      ? dateString
      : dateString + "Z";
    const date = new Date(utcDateString);
    const now = new Date();

    // Calculate difference in milliseconds
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatUserId = (userId?: string): string => {
    if (!userId) return "Anonymous";
    return userId.length > 15 ? `${userId.substring(0, 15)}...` : userId;
  };

  const handleViewConversation = (conversationId: string): void => {
    navigate(`/admin/conversations/${conversationId}`);
  };

  const handleViewAllConversations = (): void => {
    navigate("/admin/conversations");
  };

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Conversations
            </h3>
          </div>
          <button
            onClick={handleViewAllConversations}
            className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
          >
            View all
          </button>
        </div>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  User
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  First Message
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Started
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Messages
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  Feedback
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-gray-500"
                  >
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-gray-500"
                  >
                    Failed to load conversations
                  </td>
                </tr>
              ) : conversationsData?.conversations.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-gray-500"
                  >
                    No recent conversations
                  </td>
                </tr>
              ) : (
                conversationsData?.conversations.map(
                  (conversation: Conversation) => (
                    <tr key={conversation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center">
                          <User className="w-3 h-3 mr-2 text-gray-400" />
                          {formatUserId(conversation.user_id)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs">
                        <div
                          className="truncate"
                          title={
                            conversation.first_message_snippet || "No message"
                          }
                        >
                          {conversation.first_message_snippet || "No message"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                          {formatDate(conversation.started_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {conversation.message_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3 text-green-500" />
                            <span className="text-green-600 text-xs font-medium">
                              {conversation.feedback_stats?.positive_count || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ThumbsDown className="w-3 h-3 text-red-500" />
                            <span className="text-red-600 text-xs font-medium">
                              {conversation.feedback_stats?.negative_count || 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <button
                          onClick={() =>
                            handleViewConversation(conversation.id)
                          }
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                          title="View conversation"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
